import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import {
  dismissInstallHint,
  hasInstallPrompt,
  installHintKind,
  isInstallHintDismissed,
  isIosSafari,
  isStandalone,
  onInstallPromptChange,
  promptInstall,
} from "@/lib/pwa/install";
import { track } from "@/lib/tracking/analytics";

const link = "text-[13px] font-medium text-[hsl(var(--action))] hover:underline dark:text-primary";

/**
 * Phones only: "Přidejte si Zettl na plochu", once, until installed or closed.
 * Android opens the browser's install dialog; iOS Safari gets the two steps.
 */
export function InstallHint() {
  const { language } = useLanguage();
  const tr = (cs: string, en: string) => (language === "cs" ? cs : en);
  const [dismissed, setDismissed] = useState(isInstallHintDismissed);
  const [hasPrompt, setHasPrompt] = useState(hasInstallPrompt);

  useEffect(() => onInstallPromptChange(() => setHasPrompt(hasInstallPrompt())), []);

  const kind = installHintKind({
    standalone: isStandalone(),
    dismissed,
    hasPrompt,
    iosSafari:
      typeof navigator !== "undefined" &&
      isIosSafari(navigator.userAgent, navigator.maxTouchPoints ?? 0),
  });
  if (!kind) return null;

  const close = () => {
    dismissInstallHint();
    setDismissed(true);
  };

  return (
    <div
      role="status"
      className="mb-6 flex items-start gap-3 rounded-lg border bg-card px-4 py-2.5 md:hidden"
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-[13px] font-medium">
          {tr("Přidejte si Zettl na plochu", "Add Zettl to your home screen")}
        </p>
        {kind === "prompt" ? (
          <button
            type="button"
            className={link}
            onClick={async () => {
              const installed = await promptInstall();
              if (installed) {
                track("app_installed");
                close();
              }
            }}
          >
            {tr("Nainstalovat", "Install")}
          </button>
        ) : (
          <p className="text-[13px] text-muted-foreground">
            {tr(
              "Klepněte na Sdílet a pak na „Přidat na plochu“. Účtenku pak vyfotíte dvěma klepnutími.",
              "Tap Share, then “Add to Home Screen”. A receipt then takes two taps."
            )}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={close}
        className="-mr-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={tr("Zavřít", "Close")}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
